import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getTaskById, getSectionUrl, Task } from '../constants/tasks';
import { useTaskView } from '../contexts/TaskViewContext';
import { PAGE_LABELS } from '../constants/navigation';
import { getPlaceholderHint } from '../utils/answerDesign';
import { validateReadingAnswer } from '../utils/semanticValidation';
import { READING_TASKS_DATA } from '../data/tasks/reading';
import { WRITING_TASKS_DATA } from '../data/tasks/writing';
import { GRAMMAR_TASKS_DATA } from '../data/tasks/grammar';
import { VOCABULARY_TASKS_DATA } from '../data/tasks/vocabulary';

const getDraftAnswerKey = (taskId: string, questionId: string) =>
  `anglopi-draft-${taskId}-${questionId}`;

const persistDraftAnswer = (
  taskId: string,
  questionId: string,
  answer: string | string[]
) => {
  const key = getDraftAnswerKey(taskId, questionId);
  if (typeof answer === 'string') {
    if (answer.trim()) {
      localStorage.setItem(key, answer);
    } else {
      localStorage.removeItem(key);
    }
    return;
  }
  if (answer.length > 0) {
    localStorage.setItem(key, JSON.stringify(answer));
  } else {
    localStorage.removeItem(key);
  }
};

const loadSavedAnswersForTask = (
  taskId: string,
  questions: Array<{ id: string; multiSelect?: boolean }>
): { [key: string]: string | string[] } => {
  const newAnswers: { [key: string]: string | string[] } = {};
  questions.forEach((question) => {
    const draft = localStorage.getItem(getDraftAnswerKey(taskId, question.id));
    const saved = localStorage.getItem(`anglopi-answer-${taskId}-${question.id}`);
    const raw = draft ?? saved;
    if (!raw) return;
    if (question.multiSelect) {
      try {
        const parsed = JSON.parse(raw);
        newAnswers[question.id] = Array.isArray(parsed) ? parsed : raw;
      } catch {
        newAnswers[question.id] = raw;
      }
    } else {
      newAnswers[question.id] = raw;
    }
  });
  return newAnswers;
};

const clearTaskDraftAnswers = (taskId: string, questionIds: string[]) => {
  questionIds.forEach((id) => localStorage.removeItem(getDraftAnswerKey(taskId, id)));
};

interface TaskViewProps {
  section: 'grammar' | 'vocabulary' | 'reading' | 'writing';
}

const TaskView: React.FC<TaskViewProps> = ({ section }) => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { setIsInTaskView, setCurrentTaskTitle } = useTaskView();

  const [task, setTask] = useState<Task | null>(null);
  const [answers, setAnswers] = useState<{ [key: string]: string | string[] }>({});
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(() => {
    // Initialize with data from localStorage if available
    const savedCompletedTasks = localStorage.getItem('anglopi-completed-tasks');
    if (savedCompletedTasks) {
      try {
        const parsed = JSON.parse(savedCompletedTasks);
        return new Set(parsed);
      } catch (error) {
        console.error('Error parsing saved completed tasks:', error);
        return new Set();
      }
    }
    return new Set();
  });
  const [correctAnswers, setCorrectAnswers] = useState<Set<string>>(new Set());
  const [incorrectAnswers, setIncorrectAnswers] = useState<Set<string>>(new Set());
  
  const [justCompleted, setJustCompleted] = useState(false);
  const [perfectScore, setPerfectScore] = useState(false);
  const [userScore, setUserScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [selectedMadLibsTemplate, setSelectedMadLibsTemplate] = useState<any>(null);
  const [showMadLibsStory, setShowMadLibsStory] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);

  const [orderingAnswers, setOrderingAnswers] = useState<string[]>([]);
  const [shuffledOptions, setShuffledOptions] = useState<Record<string, string[]>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<string>>(new Set());
  const [dragDropState, setDragDropState] = useState<{
    available: string[];
    zones: Record<string, string[]>;
  }>({ available: [], zones: {} });
  const [selectedDragItem, setSelectedDragItem] = useState<{
    itemId: string;
    sourceZoneId: string;
  } | null>(null);
  const [useFinePointer, setUseFinePointer] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
      : true
  );
  const [isTaskResolved, setIsTaskResolved] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const onChange = () => setUseFinePointer(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);


  // Exercise data for different tasks
  const getExerciseData = useCallback(() => {
    if (!task) return { questions: [] };

    // Check for Reading tasks
    if (task.id in READING_TASKS_DATA) {
      return READING_TASKS_DATA[task.id as keyof typeof READING_TASKS_DATA];
    }

    // Check for Writing tasks
    if (task.id in WRITING_TASKS_DATA) {
      return WRITING_TASKS_DATA[task.id as keyof typeof WRITING_TASKS_DATA];
    }

    // Check for Grammar tasks
    if (task.id in GRAMMAR_TASKS_DATA) {
      return GRAMMAR_TASKS_DATA[task.id as keyof typeof GRAMMAR_TASKS_DATA];
    }

    // Check for Vocabulary tasks
    if (task.id in VOCABULARY_TASKS_DATA) {
      return VOCABULARY_TASKS_DATA[task.id as keyof typeof VOCABULARY_TASKS_DATA];
    }

    switch (task.id) {

      case 'articles-basic':
        return {
          questions: [
            {
              id: 'q1',
              text: 'I have _____ apple.',
              correctAnswer: 'an'
            },
            {
              id: 'q2',
              text: 'She is _____ teacher.',
              correctAnswer: 'a'
            },
            {
              id: 'q3',
              text: '_____ sun is bright today.',
              correctAnswer: 'The'
            },
            {
              id: 'q4',
              text: 'He bought _____ new car.',
              correctAnswer: 'a'
            },
            {
              id: 'q5',
              text: '_____ moon is beautiful tonight.',
              correctAnswer: 'The'
            }
          ]
        };

      case 'articles-advanced':
        return {
          questions: [
            {
              id: 'q1',
              text: 'I need _____ advice about this problem.',
              correctAnswer: 'no article'
            },
            {
              id: 'q2',
              text: 'She plays _____ piano beautifully.',
              correctAnswer: 'the'
            },
            {
              id: 'q3',
              text: '_____ happiness is important in life.',
              correctAnswer: 'no article'
            },
            {
              id: 'q4',
              text: 'He went to _____ hospital to visit his friend.',
              correctAnswer: 'the'
            },
            {
              id: 'q5',
              text: '_____ United States is a large country.',
              correctAnswer: 'The'
            }
          ]
        };

      default:
        return {
          questions: []
        };
    }
  }, [task]);


  // Function to cycle through mad libs stories in rotation
  const changeStory = useCallback(() => {
    const exerciseData = getExerciseData() as any;
    if (exerciseData.madLibsTemplates && exerciseData.madLibsTemplates.length > 1) {
      // Cycle to the next story in rotation (0, 1, 2, 3, 4, 5, then back to 0)
      const nextIndex = (currentStoryIndex + 1) % exerciseData.madLibsTemplates.length;
      const nextTemplate = exerciseData.madLibsTemplates[nextIndex];

      setSelectedMadLibsTemplate(nextTemplate);
      setCurrentStoryIndex(nextIndex);
      // Clear current answers when changing story
      setAnswers({});
    }
  }, [getExerciseData, currentStoryIndex]);

  const restoreCorrectAnswers = (taskData: any) => {
    // Load saved correct answers from localStorage
    const savedCorrectAnswers = localStorage.getItem(`anglopi-correct-answers-${taskData.id}`);
    if (savedCorrectAnswers) {
      try {
        const correctAnswersArray = JSON.parse(savedCorrectAnswers);
        setCorrectAnswers(new Set(correctAnswersArray));
        console.log('Restored correct answers for task:', taskData.id, correctAnswersArray);
      } catch (error) {
        console.error('Error loading saved correct answers:', error);
      }
    }
  };

  // Load task data
  useEffect(() => {
    setIsTaskResolved(false);

    if (taskId) {
      const taskData = getTaskById(taskId);
      if (taskData && taskData.section === section) {
        setTask(taskData);
        setIsInTaskView(true);
        setCurrentTaskTitle(taskData.title);
        setIsTaskResolved(true);
        console.log('TaskView - Setting context:', { isInTaskView: true, title: taskData.title });

        // Restore correct answers from previous attempts
        restoreCorrectAnswers(taskData);

        // Allow natural validation state to persist

        // For Mad Libs tasks, randomly select a template
        if (taskData.id === 'mad-libs-adventure') {
          const exerciseData = getExerciseData() as any;
          if (exerciseData.madLibsTemplates && exerciseData.madLibsTemplates.length > 0) {
            const randomIndex = Math.floor(Math.random() * exerciseData.madLibsTemplates.length);
            setSelectedMadLibsTemplate(exerciseData.madLibsTemplates[randomIndex]);
          }
        }

      } else {
        // Task not found or wrong section, redirect to section page
        setTask(null);
        setIsTaskResolved(true);
        navigate(getSectionUrl(section));
      }
    } else {
      setTask(null);
      setIsTaskResolved(true);
    }
  }, [taskId, section, navigate, setIsInTaskView, setCurrentTaskTitle, getExerciseData]);

  useEffect(() => {
    if (!task) return;
    const exerciseData = getExerciseData() as any;
    if (exerciseData.ordering?.items?.length) {
      const initialOrder = exerciseData.ordering.items.map((item: any) => item.id);
      setOrderingAnswers(shuffleArray(initialOrder));
    } else {
      setOrderingAnswers([]);
    }
    if (exerciseData.dragDrop?.items?.length) {
      setDragDropState(buildDragDropState(exerciseData.dragDrop));
    } else {
      setDragDropState({ available: [], zones: {} });
    }
    
    // Reset multiple choice pagination
    setCurrentQuestionIndex(0);

    // Shuffle options for multiple choice and multi-select questions
    if (exerciseData.questions) {
      const newShuffledOptions: Record<string, string[]> = {};
      exerciseData.questions.forEach((q: any) => {
        if (Array.isArray(q.options)) {
          newShuffledOptions[q.id] = shuffleArray([...q.options]);
        }
      });
      setShuffledOptions(newShuffledOptions);
    } else {
      setShuffledOptions({});
    }
  }, [task, getExerciseData]);

  // Cleanup context when component unmounts
  useEffect(() => {
    return () => {
      setIsInTaskView(false);
      setCurrentTaskTitle(null);
    };
  }, [setIsInTaskView, setCurrentTaskTitle]);

  // Load completed tasks from localStorage (now handled in useState initialization)
  // Removed problematic useEffect that was causing excessive re-renders

  // Check if current task is completed after completedTasks state is loaded
  useEffect(() => {
    if (task && completedTasks.has(task.id)) {
      console.log('Task is fully completed, loading score data for:', task.id);
      setJustCompleted(false); // Don't show "just completed" animation

      // Load saved score data if available
      const savedScore = localStorage.getItem(`anglopi-task-score-${task.id}`);
      console.log('Loading saved score for task:', task.id, 'savedScore:', savedScore);
      if (savedScore) {
        const scoreData = JSON.parse(savedScore);
        setUserScore(scoreData.userScore);
        setTotalQuestions(scoreData.totalQuestions);
        setPerfectScore(scoreData.perfectScore);
        console.log('Loaded score data:', scoreData);
      }
    } else if (task && !completedTasks.has(task.id)) {
      setJustCompleted(false);

      const exerciseData = getExerciseData() as any;
      if (exerciseData.questions?.length) {
        setAnswers(loadSavedAnswersForTask(task.id, exerciseData.questions));
      } else {
        setAnswers({});
      }

      const savedScore = localStorage.getItem(`anglopi-task-score-${task.id}`);
      if (savedScore) {
        try {
          const scoreData = JSON.parse(savedScore);
          setUserScore(scoreData.userScore);
          setTotalQuestions(scoreData.totalQuestions);
          setPerfectScore(scoreData.perfectScore);
          if (scoreData.totalQuestions > 0) {
            
          }
        } catch (error) {
          console.error('Error loading saved partial score:', error);
          setUserScore(0);
          setTotalQuestions(0);
          setPerfectScore(false);
        }
      } else {
        setUserScore(0);
        setTotalQuestions(0);
        setPerfectScore(false);
        
      }
    }
  }, [task?.id, completedTasks, task, getExerciseData, section]);

  // Save completed tasks to localStorage
  useEffect(() => {
    localStorage.setItem('anglopi-completed-tasks', JSON.stringify(Array.from(completedTasks)));
  }, [completedTasks]);

  // Save correct answers to localStorage
  useEffect(() => {
    if (task && correctAnswers.size > 0) {
      const correctAnswersArray = Array.from(correctAnswers);
      localStorage.setItem(`anglopi-correct-answers-${task.id}`, JSON.stringify(correctAnswersArray));
      console.log('Saving correct answers for task:', task.id, correctAnswersArray);
    }
  }, [correctAnswers, task]);

  const handleBackToSection = () => {
    navigate(getSectionUrl(section));
  };

  const handleResetTask = () => {
    if (task) {
      if (section === 'vocabulary') {
        // For vocabulary tasks: keep all answers AND feedback states
        // User can see their previous choices with visual feedback
        setJustCompleted(false);
        setPerfectScore(false);
        // Keep hasCheckedAnswers true so feedback remains visible
        // Keep correctAnswers and incorrectAnswers so user can see their results
        // Keep all answers so radio buttons remain selected
        // Don't modify answers, correctAnswers, or incorrectAnswers

        // Skip to the first incorrect question so the user doesn't scroll through correct ones
        const exerciseData = getExerciseData() as any;
        if (exerciseData.questions) {
          const firstIncorrectIndex = exerciseData.questions.findIndex(
            (q: any) => !correctAnswers.has(q.id)
          );
          setCurrentQuestionIndex(firstIncorrectIndex >= 0 ? firstIncorrectIndex : 0);
        }
      } else {
        // For other tasks: keep correct answers and their checkmarks, but clear only incorrect user input
        const currentCorrectAnswers = new Set(correctAnswers);
        const exerciseData = getExerciseData() as any;

        // Create new answers object that only keeps correct answers
        const newAnswers: { [key: string]: string | string[] } = {};
        if (exerciseData.questions) {
          exerciseData.questions.forEach((question: any) => {
            if (currentCorrectAnswers.has(question.id)) {
              // Keep the correct answer
              newAnswers[question.id] = answers[question.id] || '';
            }
          });
        }

        setAnswers(newAnswers);
        setCorrectAnswers(currentCorrectAnswers); // Preserve correct answers

        // Set pagination to the first non-correct question (skip already-correct ones)
        if (exerciseData.questions) {
          const firstIncorrectIndex = exerciseData.questions.findIndex(
            (q: any) => !currentCorrectAnswers.has(q.id)
          );
          setCurrentQuestionIndex(firstIncorrectIndex >= 0 ? firstIncorrectIndex : 0);
        } else {
          setCurrentQuestionIndex(0);
        }
        
        // Clear answered questions for anything they didn't get right
        const newAnsweredQuestions = new Set<string>();
        currentCorrectAnswers.forEach(id => newAnsweredQuestions.add(id));
        setAnsweredQuestions(newAnsweredQuestions);

        if (exerciseData.questions) {
          exerciseData.questions.forEach((question: any) => {
            if (!currentCorrectAnswers.has(question.id)) {
              localStorage.removeItem(getDraftAnswerKey(task.id, question.id));
            }
          });
        }
        setJustCompleted(false);
        setPerfectScore(false);
        setUserScore(0);
        setTotalQuestions(0);
        localStorage.removeItem(`anglopi-task-score-${task.id}`);
        if (exerciseData.ordering?.items?.length) {
          if (currentCorrectAnswers.has(exerciseData.ordering.id)) {
            setOrderingAnswers(exerciseData.ordering.correctOrder);
          } else {
            const initialOrder = exerciseData.ordering.items.map((item: any) => item.id);
            setOrderingAnswers(shuffleArray(initialOrder));
          }
        }
        if (exerciseData.dragDrop?.items?.length) {
          if (currentCorrectAnswers.has(exerciseData.dragDrop.id)) {
            setDragDropState(buildDragDropCorrectState(exerciseData.dragDrop));
          } else {
            setDragDropState(buildDragDropState(exerciseData.dragDrop));
          }
        }
      }

      // Remove from completed tasks
      setCompletedTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(task.id);
        return newSet;
      });

      // Clear saved score data
      localStorage.removeItem(`anglopi-task-score-${task.id}`);

      console.log('Task reset completed for:', task.id);
    }
  };

  const handleCompleteReset = () => {
    if (task) {
      // Complete reset - clear everything including correct answers
      setAnswers({});
      setCorrectAnswers(new Set());
      setIncorrectAnswers(new Set());
      
      setJustCompleted(false);
      setPerfectScore(false);
      setUserScore(0);
      setTotalQuestions(0);
      initializeOrdering();
      initializeDragDrop();

      // Remove from completed tasks
      setCompletedTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(task.id);
        return newSet;
      });

      // Clear all saved data
      localStorage.removeItem(`anglopi-task-score-${task.id}`);
      localStorage.removeItem(`anglopi-correct-answers-${task.id}`);

      // Clear individual answer values
      const exerciseData = getExerciseData() as any;
      if (exerciseData.questions) {
        const questionIds = exerciseData.questions.map((question: any) => question.id);
        questionIds.forEach((questionId: string) => {
          localStorage.removeItem(`anglopi-answer-${task.id}-${questionId}`);
        });
        clearTaskDraftAnswers(task.id, questionIds);
      }
    }
  };

  if (!isTaskResolved) {
    return (
      <div className="task-view">
        <div className="task-content" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="task-view">
        <div className="task-header">
          <button className="back-button" onClick={handleBackToSection}>
            <ArrowLeft size={16} />
            Back to {section.charAt(0).toUpperCase() + section.slice(1)}
          </button>
          <h1>Task Not Found</h1>
        </div>
        <div className="task-content">
          <p>The requested task could not be found.</p>
        </div>
      </div>
    );
  }

  const isCompleted = completedTasks.has(task.id);



  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));

    if (task) {
      persistDraftAnswer(task.id, questionId, answer);
    }

    // Only check individual answers in real-time for non-vocabulary tasks
    if (section !== 'vocabulary') {
      checkIndividualAnswer(questionId, answer);
    }

  };

  const toggleMultiSelectAnswer = (questionId: string, option: string) => {
    setAnswers(prev => {
      const current = Array.isArray(prev[questionId]) ? [...(prev[questionId] as string[])] : [];
      const exists = current.includes(option);
      const next = exists ? current.filter(item => item !== option) : [...current, option];
      if (task) {
        persistDraftAnswer(task.id, questionId, next);
      }
      if (section !== 'vocabulary') {
        checkIndividualAnswer(questionId, next);
      }
      return {
        ...prev,
        [questionId]: next
      };
    });
  };

  const checkIndividualAnswer = async (questionId: string, answer: string | string[]) => {
    const exerciseData = getExerciseData() as any;
    if (!exerciseData.questions || !Array.isArray(exerciseData.questions)) return;

    const question = (exerciseData.questions as any[]).find((q: any) => q.id === questionId);
    if (!question) return;

    const correctAnswers = (question as any).correctAnswers || [(question as any).correctAnswer];
    const normalizedCorrectAnswers = normalizeCorrectAnswers(correctAnswers);
    const questionType = (question as any).type || 'factual'; // Default to factual
    const hasOptions = Array.isArray((question as any).options) && (question as any).options.length > 0;
    const isMultiSelect = (question as any).multiSelect === true;

    if (hasOptions) {
      if (isMultiSelect && Array.isArray(answer)) {
        const isCorrect = areArraysEqualIgnoreOrder(answer, correctAnswers);
        if (isCorrect) {
          setCorrectAnswers(prev => {
            const newSet = new Set(prev);
            newSet.add(questionId);
            return newSet;
          });
          if (task) {
            localStorage.setItem(`anglopi-answer-${task.id}-${questionId}`, JSON.stringify(answer));
          }
        } else {
          setCorrectAnswers(prev => {
            const newSet = new Set(prev);
            newSet.delete(questionId);
            return newSet;
          });
          if (task) {
            localStorage.removeItem(`anglopi-answer-${task.id}-${questionId}`);
          }
        }
        return;
      }
      if (Array.isArray(answer)) return;
      const isCorrect = normalizedCorrectAnswers.includes(normalizeAnswerText(answer));
      if (isCorrect) {
        setCorrectAnswers(prev => {
          const newSet = new Set(prev);
          newSet.add(questionId);
          return newSet;
        });
        setIncorrectAnswers(prev => {
          const newSet = new Set(prev);
          newSet.delete(questionId);
          return newSet;
        });
        if (task) {
          localStorage.setItem(`anglopi-answer-${task.id}-${questionId}`, answer);
        }
      } else {
        setCorrectAnswers(prev => {
          const newSet = new Set(prev);
          newSet.delete(questionId);
          return newSet;
        });
        setIncorrectAnswers(prev => {
          const newSet = new Set(prev);
          newSet.add(questionId);
          return newSet;
        });
        if (task) {
          localStorage.setItem(`anglopi-answer-${task.id}-${questionId}`, answer);
        }
      }
      return;
    }

    if (Array.isArray(answer)) {
      return;
    }

    if (isSingleCharacterPartialInput(answer, correctAnswers)) {
      setCorrectAnswers(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
      if (task) {
        localStorage.removeItem(`anglopi-answer-${task.id}-${questionId}`);
      }
      return;
    }

    // Use enhanced semantic validation
    const isGrammarExercise = section === 'grammar' || section === 'writing' || section === 'vocabulary';
    const validation = await validateReadingAnswer(answer, correctAnswers, questionType, isGrammarExercise);

    if (validation.isCorrect) {
      // Add to correct answers set
      setCorrectAnswers(prev => {
        const newSet = new Set(prev);
        newSet.add(questionId);
        return newSet;
      });
      
      setIncorrectAnswers(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });

      // Save the correct answer value to localStorage
      if (task) {
        localStorage.setItem(`anglopi-answer-${task.id}-${questionId}`, answer);
      }

      // Show feedback for semantic matches
      if (validation.confidence < 1.0) {
        console.log(`Semantic match for ${questionId}: ${validation.feedback}`);
      }
    } else {
      // Remove from correct answers set if it was there before
      setCorrectAnswers(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
      
      setIncorrectAnswers(prev => {
        const newSet = new Set(prev);
        newSet.add(questionId);
        return newSet;
      });

      // Remove the answer from localStorage if it was there
      if (task) {
        localStorage.removeItem(`anglopi-answer-${task.id}-${questionId}`);
      }

      // Log suggestions for incorrect answers
      if (validation.suggestions.length > 0) {
        console.log(`Suggestions for ${questionId}: ${validation.suggestions.join(', ')}`);
      }
    }
  };

  const finalizeCheckResults = (
    newCorrectAnswers: Set<string>,
    newIncorrectAnswers: Set<string>,
    totalQuestionsCount: number
  ) => {
    setCorrectAnswers(newCorrectAnswers);
    setIncorrectAnswers(newIncorrectAnswers);
    
    setUserScore(newCorrectAnswers.size);
    setTotalQuestions(totalQuestionsCount);

    if (!task) return;

    if (newCorrectAnswers.size === totalQuestionsCount) {
      setPerfectScore(true);
      setCompletedTasks(prev => {
        const newSet = new Set(prev);
        newSet.add(task.id);
        return newSet;
      });
      setJustCompleted(true);

      const scoreData = {
        userScore: newCorrectAnswers.size,
        totalQuestions: totalQuestionsCount,
        perfectScore: true
      };
      localStorage.setItem(`anglopi-task-score-${task.id}`, JSON.stringify(scoreData));
      console.log('Saved score data for fully completed task:', scoreData, 'for task:', task.id);

      setTimeout(() => {
        setJustCompleted(false);
      }, 8000);
    } else {
      setPerfectScore(false);
      setJustCompleted(true);

      const scoreData = {
        userScore: newCorrectAnswers.size,
        totalQuestions: totalQuestionsCount,
        perfectScore: false
      };
      localStorage.setItem(`anglopi-task-score-${task.id}`, JSON.stringify(scoreData));
      console.log('Saved temporary score data for partial completion:', scoreData, 'for task:', task.id);

      setTimeout(() => {
        setJustCompleted(false);
      }, 20000);
    }
  };


  const checkAnswers = async () => {
    const exerciseData = getExerciseData() as any;
    if (exerciseData.dragDrop?.items?.length) {
      const dragDropId = exerciseData.dragDrop.id || 'drag-drop';
      const correctMapping = exerciseData.dragDrop.correctMapping || {};
      const zoneIds = Object.keys(correctMapping);
      const isCorrectMapping = zoneIds.every((zoneId: string) => {
        const expected = correctMapping[zoneId] || [];
        const actual = dragDropState.zones[zoneId] || [];
        return areArraysEqualIgnoreOrder(actual, expected);
      });
      const allItemIds = exerciseData.dragDrop.items.map((item: any) => item.id);
      const placedIds = new Set(Object.values(dragDropState.zones).flat());
      const allPlaced = allItemIds.every((id: string) => placedIds.has(id));
      const isCorrect = isCorrectMapping && allPlaced;

      const newCorrectAnswers = new Set<string>();
      const newIncorrectAnswers = new Set<string>();
      if (isCorrect) {
        newCorrectAnswers.add(dragDropId);
      }
      finalizeCheckResults(newCorrectAnswers, newIncorrectAnswers, 1);
      return;
    }

    if (exerciseData.ordering?.items?.length) {
      const orderingId = exerciseData.ordering.id || 'ordering';
      const correctOrder = exerciseData.ordering.correctOrder || [];
      const isCorrect = orderingAnswers.length === correctOrder.length &&
        orderingAnswers.every((itemId, index) => itemId === correctOrder[index]);

      const newCorrectAnswers = new Set<string>();
      const newIncorrectAnswers = new Set<string>();
      if (isCorrect) {
        newCorrectAnswers.add(orderingId);
      }
      finalizeCheckResults(newCorrectAnswers, newIncorrectAnswers, 1);
      return;
    }

    if (!exerciseData.questions) return;

    const newCorrectAnswers = new Set<string>();
    const newIncorrectAnswers = new Set<string>();

    for (const question of exerciseData.questions) {
      const isMultiSelect = (question as any).multiSelect === true;
      const storedAnswer = answers[question.id];
      if (isMultiSelect) {
        const selectedAnswers = Array.isArray(storedAnswer) ? storedAnswer : [];
        if (selectedAnswers.length === 0) continue;
        const correctAnswers = (question as any).correctAnswers || [];
        if (areArraysEqualIgnoreOrder(selectedAnswers, correctAnswers)) {
          newCorrectAnswers.add(question.id);
        } else if (section === 'vocabulary') {
          newIncorrectAnswers.add(question.id);
        }
        continue;
      }
      const userAnswer = typeof storedAnswer === 'string' ? storedAnswer.trim() : '';
      if (!userAnswer) continue;

      // Handle both single correctAnswer and array correctAnswers
      const correctAnswers = (question as any).correctAnswers || [(question as any).correctAnswer];
      const normalizedCorrectAnswers = normalizeCorrectAnswers(correctAnswers);
      const questionType = (question as any).type || 'factual';
      const hasOptions = Array.isArray((question as any).options) && (question as any).options.length > 0;

      if (hasOptions) {
        if (normalizedCorrectAnswers.includes(normalizeAnswerText(userAnswer))) {
          newCorrectAnswers.add(question.id);
        } else if (section === 'vocabulary') {
          newIncorrectAnswers.add(question.id);
        }
        continue;
      }

      if (isSingleCharacterPartialInput(userAnswer, correctAnswers)) {
        if (section === 'vocabulary') {
          newIncorrectAnswers.add(question.id);
        }
        continue;
      }

      // Use enhanced semantic validation
      const isGrammarExercise = section === 'grammar' || section === 'writing' || section === 'vocabulary';
      const validation = await validateReadingAnswer(userAnswer, correctAnswers, questionType, isGrammarExercise);
      if (validation.isCorrect) {
        newCorrectAnswers.add(question.id);
      } else {
        // For vocabulary tasks, track incorrect answers for visual feedback
        if (section === 'vocabulary') {
          newIncorrectAnswers.add(question.id);
        }
      }
    }

    // Save all current answers to localStorage
    if (task) {
      exerciseData.questions.forEach((question: any) => {
        const userAnswer = answers[question.id];
        if (userAnswer === undefined || userAnswer === '') return;
        if (Array.isArray(userAnswer)) {
          if (userAnswer.length > 0) {
            persistDraftAnswer(task.id, question.id, userAnswer);
            localStorage.setItem(`anglopi-answer-${task.id}-${question.id}`, JSON.stringify(userAnswer));
          }
          return;
        }
        const text = getAnswerText(userAnswer).trim();
        if (text) {
          persistDraftAnswer(task.id, question.id, text);
          localStorage.setItem(`anglopi-answer-${task.id}-${question.id}`, text);
        }
      });
    }

    finalizeCheckResults(newCorrectAnswers, newIncorrectAnswers, exerciseData.questions.length);
  };







  const shuffleArray = <T,>(items: T[]): T[] => {
    const result = [...items];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };

  const initializeOrdering = () => {
    const exerciseData = getExerciseData() as any;
    if (exerciseData.ordering?.items?.length) {
      const initialOrder = exerciseData.ordering.items.map((item: any) => item.id);
      setOrderingAnswers(shuffleArray(initialOrder));
    } else {
      setOrderingAnswers([]);
    }
  };

  const moveOrderingItem = (index: number, direction: -1 | 1) => {
    setOrderingAnswers(prev => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const updated = [...prev];
      [updated[index], updated[nextIndex]] = [updated[nextIndex], updated[index]];
      return updated;
    });
  };

  const areArraysEqualIgnoreOrder = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  };

  const normalizeAnswerText = (value: string): string => value.trim().toLowerCase();

  const normalizeCorrectAnswers = (values: string[]): string[] =>
    values
      .map((value) => normalizeAnswerText(String(value ?? '')))
      .filter(Boolean);

  const isSingleCharacterPartialInput = (userAnswer: string, rawCorrectAnswers: string[]): boolean => {
    const normalizedUser = normalizeAnswerText(userAnswer);
    if (normalizedUser.length !== 1) {
      return false;
    }

    const normalizedCorrect = normalizeCorrectAnswers(rawCorrectAnswers);
    if (normalizedCorrect.length === 0) {
      return false;
    }

    // Allow only if a one-character answer is actually expected.
    if (normalizedCorrect.some((answer) => answer.length === 1 && answer === normalizedUser)) {
      return false;
    }

    return normalizedCorrect.every((answer) => answer.length > 1);
  };

  const buildDragDropState = (dragDrop: any) => {
    if (!dragDrop?.items?.length || !dragDrop?.zones?.length) {
      return { available: [], zones: {} as Record<string, string[]> };
    }
    const available = dragDrop.items.map((item: any) => item.id);
    const zones = dragDrop.zones.reduce((acc: Record<string, string[]>, zone: any) => {
      acc[zone.id] = [];
      return acc;
    }, {});
    return { available, zones };
  };

  const buildDragDropCorrectState = (dragDrop: any) => {
    if (!dragDrop?.items?.length || !dragDrop?.zones?.length) {
      return { available: [], zones: {} as Record<string, string[]> };
    }
    const zones = dragDrop.zones.reduce((acc: Record<string, string[]>, zone: any) => {
      acc[zone.id] = dragDrop.correctMapping?.[zone.id] ? [...dragDrop.correctMapping[zone.id]] : [];
      return acc;
    }, {});
    const usedIds = new Set(Object.values(zones).flat());
    const available = dragDrop.items
      .map((item: any) => item.id)
      .filter((id: string) => !usedIds.has(id));
    return { available, zones };
  };

  const initializeDragDrop = () => {
    const exerciseData = getExerciseData() as any;
    setDragDropState(buildDragDropState(exerciseData.dragDrop));
  };

  const handleDragStart = (event: React.DragEvent, itemId: string, sourceZoneId: string) => {
    event.dataTransfer.setData('text/plain', JSON.stringify({ itemId, sourceZoneId }));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const moveDragItem = (itemId: string, sourceZoneId: string, targetZoneId: string) => {
    setDragDropState(prev => {
      const next = {
        available: [...prev.available],
        zones: Object.fromEntries(Object.entries(prev.zones).map(([key, value]) => [key, [...value]]))
      };

      if (sourceZoneId === 'available') {
        next.available = next.available.filter(id => id !== itemId);
      } else if (next.zones[sourceZoneId]) {
        next.zones[sourceZoneId] = next.zones[sourceZoneId].filter(id => id !== itemId);
      }

      if (targetZoneId === 'available') {
        if (!next.available.includes(itemId)) next.available.push(itemId);
      } else if (next.zones[targetZoneId]) {
        if (!next.zones[targetZoneId].includes(itemId)) next.zones[targetZoneId].push(itemId);
      }

      return next;
    });
    setSelectedDragItem(null);
  };

  const handleDrop = (event: React.DragEvent, targetZoneId: string) => {
    event.preventDefault();
    const data = event.dataTransfer.getData('text/plain');
    if (!data) return;
    const { itemId, sourceZoneId } = JSON.parse(data);
    moveDragItem(itemId, sourceZoneId, targetZoneId);
  };

  const handleDragItemTap = (itemId: string, sourceZoneId: string) => {
    if (useFinePointer) return;
    setSelectedDragItem(prev =>
      prev?.itemId === itemId && prev?.sourceZoneId === sourceZoneId
        ? null
        : { itemId, sourceZoneId }
    );
  };

  const handleZoneTap = (targetZoneId: string) => {
    if (useFinePointer || !selectedDragItem) return;
    moveDragItem(selectedDragItem.itemId, selectedDragItem.sourceZoneId, targetZoneId);
  };

  const getAnswerText = (value: string | string[] | undefined) =>
    Array.isArray(value) ? '' : value || '';

  const renderExerciseContent = () => {
    const exerciseData = getExerciseData() as any;

    if (task.id === 'mad-libs-adventure') {
      // If we don't have a selected template yet, get one immediately
      if (!selectedMadLibsTemplate) {
        const exerciseData = getExerciseData() as any;
        if (exerciseData.madLibsTemplates && exerciseData.madLibsTemplates.length > 0) {
          // Start with the first story (index 0) in the rotation
          setSelectedMadLibsTemplate(exerciseData.madLibsTemplates[0]);
          setCurrentStoryIndex(0);
          return <div className="loading">Loading Mad Libs...</div>;
        }
        return <div className="error">Mad Libs templates not found.</div>;
      }
      const allBlanksFilled = selectedMadLibsTemplate.blanks.every(
        (blank: any) => getAnswerText(answers[blank.id]).trim() !== ''
      );

      return (
        <div className="mad-libs-exercise">
          <div className="mad-libs-story-title">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-sm)' }}>
              <h3 style={{ margin: 0 }}>Story: {selectedMadLibsTemplate.title}</h3>
              <button
                onClick={changeStory}
                className="change-story-button"
                title="Get a different story"
                style={{
                  background: 'var(--color-background)',
                  color: 'var(--color-text)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                🔄 Change Story
              </button>
            </div>
            {selectedMadLibsTemplate.image && (
              <div className="mad-libs-story-image">
                <img
                  src={selectedMadLibsTemplate.image}
                  alt={selectedMadLibsTemplate.imageAlt || selectedMadLibsTemplate.title}
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-md)',
                    margin: 'var(--spacing-md) 0',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </div>
            )}
          </div>
          <div className="mad-libs-blanks">
            {selectedMadLibsTemplate.blanks.map((blank: any) => (
              <div key={blank.id} className="mad-lib-input">
                <label htmlFor={blank.id}>{blank.label}</label>
                <input
                  type="text"
                  id={blank.id}
                  className="mad-lib-field"
                  placeholder={blank.placeholder}
                  value={answers[blank.id] || ''}
                  onChange={(e) => handleAnswerChange(blank.id, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="mad-libs-actions">
            <button
              className="generate-story-btn"
              onClick={() => setShowMadLibsStory(true)}
              disabled={!allBlanksFilled}
              style={{
                background: allBlanksFilled ? 'var(--color-accent)' : '#ccc',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: allBlanksFilled ? 'pointer' : 'not-allowed',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500',
                marginTop: 'var(--spacing-lg)',
                marginRight: 'var(--spacing-md)'
              }}
            >
              {allBlanksFilled ? 'Generate My Story!' : 'Fill in all blanks to generate story'}
            </button>

            <button
              className="browse-story-btn"
              onClick={() => {
                const exerciseData = getExerciseData() as any;
                if (exerciseData.madLibsTemplates && exerciseData.madLibsTemplates.length > 0) {
                  const randomIndex = Math.floor(Math.random() * exerciseData.madLibsTemplates.length);
                  setSelectedMadLibsTemplate(exerciseData.madLibsTemplates[randomIndex]);
                  setShowMadLibsStory(false);
                  setAnswers({});
                }
              }}
              style={{
                background: 'var(--color-accent-hover)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500',
                marginTop: 'var(--spacing-lg)'
              }}
            >
              Try Another Story
            </button>
          </div>

          {showMadLibsStory && (
            <div className="mad-libs-story">
              <h3>Your Completed Story:</h3>
              <div className="story-text">
                {selectedMadLibsTemplate.story.split(/\[([^\]]+)\]/).map((part: any, index: number) => {
                  if (index % 2 === 1) {
                    // part is exactly the blank id (e.g. "adjective1", "verb3", "place")
                    const userAnswer = answers[part];
                    return (
                      <span key={index} className="story-blank">
                        <strong>{userAnswer || `[${part}]`}</strong>
                      </span>
                    );
                  }
                  return <span key={index}>{part}</span>;
                })}
              </div>
              <button
                className="new-story-btn"
                onClick={() => {
                  setShowMadLibsStory(false);
                  setAnswers({});
                  // Select a new random story
                  if (exerciseData.madLibsTemplates && exerciseData.madLibsTemplates.length > 0) {
                    const randomIndex = Math.floor(Math.random() * exerciseData.madLibsTemplates.length);
                    setSelectedMadLibsTemplate(exerciseData.madLibsTemplates[randomIndex]);
                  }
                }}
                style={{
                  background: 'var(--color-accent-hover)',
                  color: 'white',
                  border: 'none',
                  padding: 'var(--spacing-sm) var(--spacing-lg)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-body)',
                  fontWeight: '500',
                  marginTop: 'var(--spacing-lg)'
                }}
              >
                Try a New Story
              </button>
            </div>
          )}
        </div>
      );
    } else if (task.type === 'fill-in-the-blanks') {
      const questionList = exerciseData.questions || [];
      const question = questionList[currentQuestionIndex];
      if (!question) return null;

      const remainingQuestions = questionList.filter((q: any) => !correctAnswers.has(q.id) || q.id === question.id);
      const currentRemainingIndex = remainingQuestions.findIndex((q: any) => q.id === question.id);
      const nextUnansweredIndex = questionList.findIndex((q: any, i: number) => i > currentQuestionIndex && !correctAnswers.has(q.id));
      const isLastQuestion = nextUnansweredIndex === -1 && (currentQuestionIndex >= questionList.length - 1 || questionList.slice(currentQuestionIndex + 1).every((q: any) => correctAnswers.has(q.id)));
      const placeholderHint = getPlaceholderHint(question);
      const hasAnswered = answeredQuestions.has(question.id);
      const isCorrect = correctAnswers.has(question.id);
      const isIncorrect = incorrectAnswers.has(question.id);
      
      return (
        <div>
          <div className="progress-indicator" style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-text-light)', fontWeight: 500 }}>
            Question {correctAnswers.size > 0 && remainingQuestions.length < questionList.length ? `${currentRemainingIndex + 1} of ${remainingQuestions.length} remaining` : `${currentQuestionIndex + 1} of ${questionList.length}`}
          </div>
          
          <div key={question.id} className="question">
            <div className="fill-blank-container">
              <p>
                {question.text.split('_____').map((part: any, i: number) => (
                  <span key={i}>
                    {part}
                    {i < question.text.split('_____').length - 1 && (
                      <input
                        type="text"
                        className="answer-input-small"
                        value={getAnswerText(answers[question.id])}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder={placeholderHint}
                        disabled={hasAnswered}
                      />
                    )}
                  </span>
                ))}
                {hasAnswered && (
                  <>
                    {isCorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#10b981', marginLeft: '8px' }}>✓</span>
                    )}
                    {isIncorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#ef4444', marginLeft: '8px' }}>✕</span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
          
          {!hasAnswered ? (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (answers[question.id] && answers[question.id].length > 0) {
                  setAnsweredQuestions(prev => new Set(prev).add(question.id));
                }
              }}
              disabled={!answers[question.id] || answers[question.id].length === 0}
              style={{
                background: answers[question.id] && answers[question.id].length > 0 ? 'var(--color-accent)' : 'var(--color-border)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: answers[question.id] && answers[question.id].length > 0 ? 'pointer' : 'not-allowed',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              Submit Answer
            </button>
          ) : (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (isLastQuestion) {
                  checkAnswers();
                } else {
                  setCurrentQuestionIndex(nextUnansweredIndex >= 0 ? nextUnansweredIndex : currentQuestionIndex + 1);
                }
              }}
              style={{
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              {isLastQuestion ? 'Finish' : 'Next'}
            </button>
          )}
        </div>
      );
    } else if (task.type === 'matching') {
      const questionList = exerciseData.questions || [];
      const question = questionList[currentQuestionIndex];
      if (!question) return null;

      const remainingQuestions = questionList.filter((q: any) => !correctAnswers.has(q.id) || q.id === question.id);
      const currentRemainingIndex = remainingQuestions.findIndex((q: any) => q.id === question.id);
      const nextUnansweredIndex = questionList.findIndex((q: any, i: number) => i > currentQuestionIndex && !correctAnswers.has(q.id));
      const isLastQuestion = nextUnansweredIndex === -1 && (currentQuestionIndex >= questionList.length - 1 || questionList.slice(currentQuestionIndex + 1).every((q: any) => correctAnswers.has(q.id)));
      const hasAnswered = answeredQuestions.has(question.id);
      const isCorrect = correctAnswers.has(question.id);
      const isIncorrect = incorrectAnswers.has(question.id);

      return (
        <div className="matching-exercise">
          {exerciseData.instructions && (
            <div className="writing-instructions">
              <h3>Instructions</h3>
              <p>{exerciseData.instructions}</p>
            </div>
          )}
          
          <div className="progress-indicator" style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-text-light)', fontWeight: 500 }}>
            Question {correctAnswers.size > 0 && remainingQuestions.length < questionList.length ? `${currentRemainingIndex + 1} of ${remainingQuestions.length} remaining` : `${currentQuestionIndex + 1} of ${questionList.length}`}
          </div>
          
          <div key={question.id} className="question">
            <div className="question-with-checkmark">
              <p>
                {question.text}
                {hasAnswered && (
                  <>
                    {isCorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#10b981', marginLeft: '8px' }}>✓</span>
                    )}
                    {isIncorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#ef4444', marginLeft: '8px' }}>✕</span>
                    )}
                  </>
                )}
              </p>
            </div>
            <select
              className="answer-select"
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              disabled={hasAnswered}
            >
              <option value="">Choose a match...</option>
              {(shuffledOptions[question.id] || question.options)?.map((option: any) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          
          {!hasAnswered ? (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (answers[question.id] && answers[question.id].length > 0) {
                  setAnsweredQuestions(prev => new Set(prev).add(question.id));
                }
              }}
              disabled={!answers[question.id] || answers[question.id].length === 0}
              style={{
                background: answers[question.id] && answers[question.id].length > 0 ? 'var(--color-accent)' : 'var(--color-border)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: answers[question.id] && answers[question.id].length > 0 ? 'pointer' : 'not-allowed',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              Submit Answer
            </button>
          ) : (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (isLastQuestion) {
                  checkAnswers();
                } else {
                  setCurrentQuestionIndex(nextUnansweredIndex >= 0 ? nextUnansweredIndex : currentQuestionIndex + 1);
                }
              }}
              style={{
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              {isLastQuestion ? 'Finish' : 'Next'}
            </button>
          )}
        </div>
      );
    } else if (task.type === 'ordering') {
      const orderingData = exerciseData.ordering;
      if (!orderingData?.items?.length) {
        return <div className="error">Ordering data not found.</div>;
      }
      const itemsById = orderingData.items.reduce((acc: any, item: any) => {
        acc[item.id] = item;
        return acc;
      }, {});
      return (
        <div className="ordering-exercise">
          {exerciseData.instructions && (
            <div className="writing-instructions">
              <h3>Instructions</h3>
              <p>{exerciseData.instructions}</p>
            </div>
          )}
          <ol className="ordering-list">
            {orderingAnswers.map((itemId, index) => (
              <li key={itemId} className="ordering-item">
                <div className="ordering-item-text">{itemsById[itemId]?.text}</div>
                <div className="ordering-item-controls">
                  <button
                    type="button"
                    className="ordering-move-btn"
                    onClick={() => moveOrderingItem(index, -1)}
                    disabled={index === 0}
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="ordering-move-btn"
                    onClick={() => moveOrderingItem(index, 1)}
                    disabled={index === orderingAnswers.length - 1}
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                </div>
              </li>
            ))}
          </ol>
          {correctAnswers.has(orderingData.id) && (
            <div className="checkmark" style={{ marginTop: 'var(--spacing-sm)' }}>✓ Correct order!</div>
          )}
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-lg)' }}>
            <button
              className="check-answers-btn"
              onClick={checkAnswers}
              style={{
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              Check Answers
            </button>
            <button
              onClick={initializeOrdering}
              style={{
                background: 'var(--color-accent-hover)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              Shuffle Order
            </button>
          </div>
        </div>
      );
    } else if (task.type === 'multi-select') {
      const questionList = exerciseData.questions || [];
      const question = questionList[currentQuestionIndex];
      if (!question) return null;

      const remainingQuestions = questionList.filter((q: any) => !correctAnswers.has(q.id) || q.id === question.id);
      const currentRemainingIndex = remainingQuestions.findIndex((q: any) => q.id === question.id);
      const nextUnansweredIndex = questionList.findIndex((q: any, i: number) => i > currentQuestionIndex && !correctAnswers.has(q.id));
      const isLastQuestion = nextUnansweredIndex === -1 && (currentQuestionIndex >= questionList.length - 1 || questionList.slice(currentQuestionIndex + 1).every((q: any) => correctAnswers.has(q.id)));
      const selectedAnswers = Array.isArray(answers[question.id]) ? (answers[question.id] as string[]) : [];
      const hasAnswered = answeredQuestions.has(question.id);
      const isCorrect = correctAnswers.has(question.id);
      const isIncorrect = incorrectAnswers.has(question.id);

      return (
        <div className="multi-select-exercise">
          {exerciseData.instructions && (
            <div className="writing-instructions">
              <h3>Instructions</h3>
              <p>{exerciseData.instructions}</p>
            </div>
          )}
          
          <div className="progress-indicator" style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-text-light)', fontWeight: 500 }}>
            Question {correctAnswers.size > 0 && remainingQuestions.length < questionList.length ? `${currentRemainingIndex + 1} of ${remainingQuestions.length} remaining` : `${currentQuestionIndex + 1} of ${questionList.length}`}
          </div>
          
          <div key={question.id} className="question">
            <div className="question-with-checkmark">
              <p>
                {question.text}
                {hasAnswered && (
                  <>
                    {isCorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#10b981', marginLeft: '8px' }}>✓</span>
                    )}
                    {isIncorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#ef4444', marginLeft: '8px' }}>✕</span>
                    )}
                  </>
                )}
              </p>
            </div>
            <div className="options">
              {(shuffledOptions[question.id] || question.options)?.map((option: any) => {
                const isOptionSelected = selectedAnswers.includes(option);
                const isOptionCorrect = question.correctAnswers ? question.correctAnswers.includes(option) : option === question.correctAnswer;
                
                return (
                <label key={option} style={{ opacity: hasAnswered && !isOptionSelected ? 0.6 : 1 }}>
                  <input
                    type="checkbox"
                    checked={isOptionSelected}
                    onChange={() => toggleMultiSelectAnswer(question.id, option)}
                    disabled={hasAnswered}
                  />
                  {option}
                  {hasAnswered && isOptionCorrect && (
                    <span className="checkmark checkmark-inline" style={{ color: '#10b981', marginLeft: '8px', fontSize: '18px' }}>✓</span>
                  )}
                  {hasAnswered && isOptionSelected && !isOptionCorrect && (
                    <span className="checkmark checkmark-inline" style={{ color: '#ef4444', marginLeft: '8px', fontSize: '18px' }}>✕</span>
                  )}
                </label>
              )})}
            </div>
          </div>
          
          {!hasAnswered ? (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (selectedAnswers.length > 0) {
                  setAnsweredQuestions(prev => new Set(prev).add(question.id));
                }
              }}
              disabled={selectedAnswers.length === 0}
              style={{
                background: selectedAnswers.length > 0 ? 'var(--color-accent)' : 'var(--color-border)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: selectedAnswers.length > 0 ? 'pointer' : 'not-allowed',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              Submit Answer
            </button>
          ) : (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (isLastQuestion) {
                  checkAnswers();
                } else {
                  setCurrentQuestionIndex(nextUnansweredIndex >= 0 ? nextUnansweredIndex : currentQuestionIndex + 1);
                }
              }}
              style={{
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              {isLastQuestion ? 'Finish' : 'Next'}
            </button>
          )}
        </div>
      );
    } else if (task.type === 'drag-and-drop') {
      const dragDropData = exerciseData.dragDrop;
      if (!dragDropData?.items?.length) {
        return <div className="error">Drag-and-drop data not found.</div>;
      }
      const itemsById = dragDropData.items.reduce((acc: any, item: any) => {
        acc[item.id] = item;
        return acc;
      }, {});
      return (
        <div className="drag-drop-exercise">
          {exerciseData.instructions && (
            <div className="writing-instructions">
              <h3>Instructions</h3>
              <p>{exerciseData.instructions}</p>
            </div>
          )}
          {!useFinePointer && (
            <p className="drag-drop-tap-hint">Tap a sentence, then tap a zone to place it.</p>
          )}
          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <h4>Available Sentences</h4>
            <div
              className="drag-drop-zone"
              onDragOver={useFinePointer ? handleDragOver : undefined}
              onDrop={useFinePointer ? (event) => handleDrop(event, 'available') : undefined}
              onClick={() => handleZoneTap('available')}
            >
              {dragDropState.available.length === 0 && (
                <div style={{ color: 'var(--color-text-muted)' }}>All sentences placed</div>
              )}
              {dragDropState.available.map((itemId) => (
                <div
                  key={itemId}
                  className={`drag-drop-item ${!useFinePointer ? 'drag-drop-item-coarse' : 'drag-drop-item-fine'} ${
                    selectedDragItem?.itemId === itemId && selectedDragItem?.sourceZoneId === 'available'
                      ? 'drag-drop-item-selected'
                      : ''
                  }`}
                  draggable={useFinePointer}
                  onDragStart={useFinePointer ? (event) => handleDragStart(event, itemId, 'available') : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDragItemTap(itemId, 'available');
                  }}
                >
                  {itemsById[itemId]?.text}
                </div>
              ))}
            </div>
          </div>
          <div className="drag-drop-zones-grid">
            {dragDropData.zones.map((zone: any) => (
              <div key={zone.id}>
                <h4>{zone.label}</h4>
                <div
                  className="drag-drop-zone drag-drop-zone-drop"
                  onDragOver={useFinePointer ? handleDragOver : undefined}
                  onDrop={useFinePointer ? (event) => handleDrop(event, zone.id) : undefined}
                  onClick={() => handleZoneTap(zone.id)}
                >
                  {(dragDropState.zones[zone.id] || []).map((itemId) => (
                    <div
                      key={itemId}
                      className={`drag-drop-item ${!useFinePointer ? 'drag-drop-item-coarse' : 'drag-drop-item-fine'} ${
                        selectedDragItem?.itemId === itemId && selectedDragItem?.sourceZoneId === zone.id
                          ? 'drag-drop-item-selected'
                          : ''
                      }`}
                      draggable={useFinePointer}
                      onDragStart={useFinePointer ? (event) => handleDragStart(event, itemId, zone.id) : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDragItemTap(itemId, zone.id);
                      }}
                    >
                      {itemsById[itemId]?.text}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {correctAnswers.has(dragDropData.id) && (
            <div className="checkmark" style={{ marginTop: 'var(--spacing-sm)' }}>✓ Correct placement!</div>
          )}
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-lg)' }}>
            <button
              className="check-answers-btn"
              onClick={checkAnswers}
              style={{
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              Check Answers
            </button>
            <button
              onClick={initializeDragDrop}
              style={{
                background: 'var(--color-accent-hover)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              Reset Placement
            </button>
          </div>
        </div>
      );
    } else if (task.type === 'single-select') {
      const questionList = exerciseData.questions || [];
      const question = questionList[currentQuestionIndex];
      if (!question) return null;

      const remainingQuestions = questionList.filter((q: any) => !correctAnswers.has(q.id) || q.id === question.id);
      const currentRemainingIndex = remainingQuestions.findIndex((q: any) => q.id === question.id);
      const nextUnansweredIndex = questionList.findIndex((q: any, i: number) => i > currentQuestionIndex && !correctAnswers.has(q.id));
      const isLastQuestion = nextUnansweredIndex === -1 && (currentQuestionIndex >= questionList.length - 1 || questionList.slice(currentQuestionIndex + 1).every((q: any) => correctAnswers.has(q.id)));
      const hasAnswered = answeredQuestions.has(question.id);
      const isCorrect = correctAnswers.has(question.id);
      const isIncorrect = incorrectAnswers.has(question.id);

      return (
        <div>
          <div className="progress-indicator" style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-text-light)', fontWeight: 500 }}>
            Question {correctAnswers.size > 0 && remainingQuestions.length < questionList.length ? `${currentRemainingIndex + 1} of ${remainingQuestions.length} remaining` : `${currentQuestionIndex + 1} of ${questionList.length}`}
          </div>
          <div key={question.id} className="question">
            <div className="question-with-checkmark">
              <p>
                {question.text}
                {hasAnswered && (
                  <>
                    {isCorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#10b981', marginLeft: '8px' }}>✓</span>
                    )}
                    {isIncorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#ef4444', marginLeft: '8px' }}>✕</span>
                    )}
                  </>
                )}
              </p>
            </div>
            <div className="options">
              {(shuffledOptions[question.id] || question.options)?.map((option: any) => {
                const isOptionSelected = getAnswerText(answers[question.id]) === option;
                const isOptionCorrect = question.correctAnswers ? question.correctAnswers.includes(option) : option === question.correctAnswer;
                
                return (
                <label key={option} style={{ opacity: hasAnswered && !isOptionSelected ? 0.6 : 1 }}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option}
                    checked={isOptionSelected}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    disabled={hasAnswered}
                  />
                  {option}
                  {hasAnswered && isOptionCorrect && (
                    <span className="checkmark checkmark-inline" style={{ color: '#10b981', marginLeft: '8px', fontSize: '18px' }}>✓</span>
                  )}
                  {hasAnswered && isOptionSelected && !isOptionCorrect && (
                    <span className="checkmark checkmark-inline" style={{ color: '#ef4444', marginLeft: '8px', fontSize: '18px' }}>✕</span>
                  )}
                </label>
              )})}
            </div>
          </div>
          
          {!hasAnswered ? (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (answers[question.id]) {
                  setAnsweredQuestions(prev => new Set(prev).add(question.id));
                }
              }}
              disabled={!answers[question.id]}
              style={{
                background: answers[question.id] ? 'var(--color-accent)' : 'var(--color-border)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: answers[question.id] ? 'pointer' : 'not-allowed',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              Submit Answer
            </button>
          ) : (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (isLastQuestion) {
                  checkAnswers();
                } else {
                  setCurrentQuestionIndex(nextUnansweredIndex >= 0 ? nextUnansweredIndex : currentQuestionIndex + 1);
                }
              }}
              style={{
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              {isLastQuestion ? 'Finish' : 'Next'}
            </button>
          )}
        </div>
      );
    } else if (task.type === 'inline-select') {
      const questionList = exerciseData.questions || [];
      const question = questionList[currentQuestionIndex];
      if (!question) return null;

      const remainingQuestions = questionList.filter((q: any) => !correctAnswers.has(q.id) || q.id === question.id);
      const currentRemainingIndex = remainingQuestions.findIndex((q: any) => q.id === question.id);
      const nextUnansweredIndex = questionList.findIndex((q: any, i: number) => i > currentQuestionIndex && !correctAnswers.has(q.id));
      const isLastQuestion = nextUnansweredIndex === -1 && (currentQuestionIndex >= questionList.length - 1 || questionList.slice(currentQuestionIndex + 1).every((q: any) => correctAnswers.has(q.id)));
      const hasAnswered = answeredQuestions.has(question.id);
      const isCorrect = correctAnswers.has(question.id);
      const isIncorrect = incorrectAnswers.has(question.id);

      return (
        <div>
          <div className="progress-indicator" style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-text-light)', fontWeight: 500 }}>
            Question {correctAnswers.size > 0 && remainingQuestions.length < questionList.length ? `${currentRemainingIndex + 1} of ${remainingQuestions.length} remaining` : `${currentQuestionIndex + 1} of ${questionList.length}`}
          </div>
          <div key={question.id} className="question">
            <div className="fill-blank-container">
              <p>
                {question.text.split('_____').map((part: any, i: number) => (
                  <span key={i}>
                    {part}
                    {i < question.text.split('_____').length - 1 && (
                      <select
                        className="answer-select"
                        value={getAnswerText(answers[question.id])}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        disabled={hasAnswered}
                      >
                        <option value="">Choose...</option>
                        {(shuffledOptions[question.id] || question.options)?.map((option: any) => (
                          <option key={option} value={option}>
                            {option || '(no article)'}
                          </option>
                        ))}
                      </select>
                    )}
                  </span>
                ))}
                {hasAnswered && (
                  <>
                    {isCorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#10b981', marginLeft: '8px' }}>✓</span>
                    )}
                    {isIncorrect && (
                      <span className="checkmark checkmark-inline" style={{ color: '#ef4444', marginLeft: '8px' }}>✕</span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
          
          {!hasAnswered ? (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (answers[question.id]) {
                  setAnsweredQuestions(prev => new Set(prev).add(question.id));
                }
              }}
              disabled={!answers[question.id]}
              style={{
                background: answers[question.id] ? 'var(--color-accent)' : 'var(--color-border)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: answers[question.id] ? 'pointer' : 'not-allowed',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              Submit Answer
            </button>
          ) : (
            <button
              className="check-answers-btn"
              onClick={() => {
                if (isLastQuestion) {
                  checkAnswers();
                } else {
                  setCurrentQuestionIndex(nextUnansweredIndex >= 0 ? nextUnansweredIndex : currentQuestionIndex + 1);
                }
              }}
              style={{
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                marginTop: 'var(--spacing-lg)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '500'
              }}
            >
              {isLastQuestion ? 'Finish' : 'Next'}
            </button>
          )}
        </div>
      );
    } else if (task.type === 'reading-comprehension') {
      const questionList = exerciseData.questions || [];
      const question = questionList[currentQuestionIndex];
      const remainingQuestions = questionList.filter((q: any) => !correctAnswers.has(q.id) || q.id === question?.id);
      const currentRemainingIndex = question ? remainingQuestions.findIndex((q: any) => q.id === question.id) : 0;
      const nextUnansweredIndex = questionList.findIndex((q: any, i: number) => i > currentQuestionIndex && !correctAnswers.has(q.id));
      const isLastQuestion = nextUnansweredIndex === -1 && (currentQuestionIndex >= questionList.length - 1 || questionList.slice(currentQuestionIndex + 1).every((q: any) => correctAnswers.has(q.id)));
      
      return (
        <div className="reading-exercise">
          {exerciseData.passage && (
            <div className="reading-passage">
              <h3>Reading Passage</h3>
              <div className="passage-content">
                {exerciseData.passage.split('\n\n').map((paragraph: string, index: number) => (
                  <p key={index} className="passage-paragraph">
                    {paragraph}
                  </p>
                ))}
              </div>
              {exerciseData.source && (
                <div className="passage-source">
                  <p><strong>Source:</strong> {exerciseData.source.title} by {exerciseData.source.author} ({exerciseData.source.year})</p>
                  <p><em>{exerciseData.source.source}</em></p>
                </div>
              )}
            </div>
          )}

          {question && (
            <div className="reading-questions">
              <h3>Comprehension Questions</h3>
              <div className="progress-indicator" style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-text-light)', fontWeight: 500 }}>
                Question {correctAnswers.size > 0 && remainingQuestions.length < questionList.length ? `${currentRemainingIndex + 1} of ${remainingQuestions.length} remaining` : `${currentQuestionIndex + 1} of ${questionList.length}`}
              </div>
              
              <div key={question.id} className="question">
                <div className="question-with-checkmark">
                  <p>
                    {question.text}
                    {answeredQuestions.has(question.id) && (
                      <>
                        {correctAnswers.has(question.id) && (
                          <span className="checkmark checkmark-inline" style={{ color: '#10b981', marginLeft: '8px' }}>✓</span>
                        )}
                        {incorrectAnswers.has(question.id) && (
                          <span className="checkmark checkmark-inline" style={{ color: '#ef4444', marginLeft: '8px' }}>✕</span>
                        )}
                      </>
                    )}
                  </p>
                </div>
                {Array.isArray(question.options) && question.options.length > 0 ? (
                  <div className="options">
                    {(shuffledOptions[question.id] || question.options).map((option: any) => (
                      <label key={option} style={{ opacity: answeredQuestions.has(question.id) && getAnswerText(answers[question.id]) !== option ? 0.6 : 1 }}>
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          value={option}
                          checked={getAnswerText(answers[question.id]) === option}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          disabled={answeredQuestions.has(question.id)}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="input-with-checkmark">
                    <input
                      type="text"
                      value={getAnswerText(answers[question.id])}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      placeholder="Type your answer here..."
                      className="answer-input"
                      disabled={answeredQuestions.has(question.id)}
                    />
                  </div>
                )}
              </div>
              
              {!answeredQuestions.has(question.id) ? (
                <button
                  className="check-answers-btn"
                  onClick={() => {
                    if (answers[question.id] && answers[question.id].length > 0) {
                      setAnsweredQuestions(prev => new Set(prev).add(question.id));
                    }
                  }}
                  disabled={!answers[question.id] || answers[question.id].length === 0}
                  style={{
                    background: answers[question.id] && answers[question.id].length > 0 ? 'var(--color-accent)' : 'var(--color-border)',
                    color: 'white',
                    border: 'none',
                    padding: 'var(--spacing-sm) var(--spacing-lg)',
                    borderRadius: 'var(--radius-md)',
                    cursor: answers[question.id] && answers[question.id].length > 0 ? 'pointer' : 'not-allowed',
                    marginTop: 'var(--spacing-lg)',
                    fontSize: 'var(--font-size-body)',
                    fontWeight: '500'
                  }}
                >
                  Submit Answer
                </button>
              ) : (
                <button
                  className="check-answers-btn"
                  onClick={() => {
                    if (isLastQuestion) {
                      checkAnswers();
                    } else {
                      setCurrentQuestionIndex(nextUnansweredIndex >= 0 ? nextUnansweredIndex : currentQuestionIndex + 1);
                    }
                  }}
                  style={{
                    background: 'var(--color-accent)',
                    color: 'white',
                    border: 'none',
                    padding: 'var(--spacing-sm) var(--spacing-lg)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    marginTop: 'var(--spacing-lg)',
                    fontSize: 'var(--font-size-body)',
                    fontWeight: '500'
                  }}
                >
                  {isLastQuestion ? 'Finish' : 'Next'}
                </button>
              )}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="coming-soon">
          <h4>Exercise Content Coming Soon</h4>
          <p>This task is part of the {section} section. The interactive exercise will be implemented soon!</p>
          <p>For now, you can explore the task structure and difficulty level.</p>
        </div>
      );
    }
  };

  return (
    <div className="task-view">
      <div className="task-header">
        {!isCompleted && !justCompleted && (
          <button
            className="return-to-section-button return-to-section-button--header"
            onClick={handleBackToSection}
            aria-label="Return to section"
          >
            <ArrowLeft size={18} />
            <span>Back to {PAGE_LABELS[section]}</span>
          </button>
        )}
        {(isCompleted || justCompleted) && <span className="completed-text">Completed</span>}
      </div>

      <div className="task-content">
        {(isCompleted || justCompleted) && (
          <div className="task-completion">
            <div className={`completion-message ${perfectScore ? 'perfect' : `score-${userScore}-${totalQuestions}`}`}>
                <div className="completion-icon">
                  {perfectScore ? '★' :
                    userScore === 0 ? '●' :
                      userScore <= totalQuestions * 0.3 ? '◆' :
                        userScore <= totalQuestions * 0.6 ? '▲' :
                          userScore <= totalQuestions * 0.8 ? '♦' : '◈'}
                </div>
                <h3>
                  {perfectScore ? 'Task Completed' :
                    userScore === 0 ? 'Try Again' :
                      userScore <= totalQuestions * 0.3 ? 'Getting Started' :
                        userScore <= totalQuestions * 0.6 ? 'Good Progress' :
                          userScore <= totalQuestions * 0.8 ? 'Great Work' : 'Almost There'}
                </h3>
                <p>
                  {perfectScore ? 'Well done! You completed this task successfully.' :
                    userScore === 0 ? 'Keep practicing to improve your score.' :
                      userScore <= totalQuestions * 0.3 ? 'You\'re making progress. Continue practicing.' :
                        userScore <= totalQuestions * 0.6 ? 'Good work! A few more correct answers will help.' :
                          userScore <= totalQuestions * 0.8 ? 'Excellent progress! You\'re very close to completion.' : 'Outstanding! Just a couple more correct answers needed.'}
                </p>
                <div className="score-display">
                  <span className="score-text">Score: {userScore}/{totalQuestions}</span>
                  {perfectScore && <span className="perfect-score">Perfect Score</span>}
                  {!perfectScore && userScore > 0 && <span className="partial-score">
                    {userScore <= totalQuestions * 0.3 ? 'Keep practicing' :
                      userScore <= totalQuestions * 0.6 ? 'Improving well' :
                        userScore <= totalQuestions * 0.8 ? 'Almost there' : 'Very close'}
                  </span>}
                </div>
                <div className="completion-buttons" style={{
                  justifyContent: 'center',
                  gap: 'var(--spacing-sm)',
                  flexWrap: 'wrap',
                  maxWidth: '100%'
                }}>
                  {!perfectScore && (
                    <button className="reset-button" onClick={handleResetTask} style={{
                      padding: 'var(--spacing-sm) var(--spacing-md)',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap',
                      flex: 'none'
                    }}>
                      Keep Going
                    </button>
                  )}
                  <button className="complete-reset-button" onClick={handleCompleteReset} style={{
                    background: 'var(--color-text-muted)',
                    color: 'white',
                    border: 'none',
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: '500',
                    flex: 'none',
                    whiteSpace: 'nowrap'
                  }}>
                    Start Fresh
                  </button>
                  <button
                    className="back-to-tasks-button"
                    onClick={handleBackToSection}
                    style={{
                      flex: 'none',
                      minWidth: 'auto'
                    }}
                  >
                    Back to {PAGE_LABELS[section]}
                  </button>
                </div>
            </div>
          </div>
        )}

        {!isCompleted && !justCompleted && (
          <div className="task-exercise">
            <div className="exercise-instructions">
              <h1>{task.title}</h1>
              <p><strong>Difficulty:</strong> {task.difficulty} (Level {task.level})</p>

            </div>

            <div className="exercise-questions">
              {renderExerciseContent()}
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default TaskView;
